import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./en.json";
import es from "./es.json";
import termsEn from "./terms.en.json";
import termsEs from "./terms.es.json";

const LS_KEY = "laguna.locale";
const IS_DEV = process.env.NODE_ENV !== "production";

export const SUPPORTED_LOCALES = ["en", "es"];

/*
 * Missing-key semantics (Phase 1.5a update):
 *
 * - In production: if a key is missing in the active locale, fall back
 *   silently to the EN value. Never render a raw key or a broken
 *   "MISSING" marker to end-users.
 * - In development: same EN fallback at render time BUT also emit a
 *   visible yellow "⚠ MISSING:" marker + console.warn once per key so
 *   gaps surface during dev without ever shipping to prod.
 *
 * Implementation: i18next's built-in fallbackLng='en' + `returnFallback`
 * handles the EN fallback. We only override `parseMissingKeyHandler`
 * (used when the key is missing in BOTH active AND fallback locale) to
 * tag dev markers.
 */
const missingKeysSeen = new Set();

function detectInitialLocale() {
    const stored =
        typeof window !== "undefined" ? window.localStorage.getItem(LS_KEY) : null;
    if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
    const nav =
        typeof navigator !== "undefined" ? navigator.language || "en" : "en";
    return nav.toLowerCase().startsWith("es") ? "es" : "en";
}

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources: {
            en: { translation: en, terms: termsEn },
            es: { translation: es, terms: termsEs },
        },
        lng: detectInitialLocale(),
        // Cascade: active locale →︎ English →︎ key path. The cascade is
        // transparent to components — a Spanish component that references
        // a key not yet translated will render the English value.
        fallbackLng: "en",
        supportedLngs: SUPPORTED_LOCALES,
        interpolation: { escapeValue: false },
        detection: {
            order: ["localStorage", "navigator"],
            lookupLocalStorage: LS_KEY,
            caches: ["localStorage"],
        },
        returnObjects: true,
        returnEmptyString: false,
        saveMissing: IS_DEV,
        missingKeyHandler: (lngs, ns, key) => {
            if (missingKeysSeen.has(key)) return;
            missingKeysSeen.add(key);
            if (IS_DEV && typeof console !== "undefined") {
                // eslint-disable-next-line no-console
                console.warn(
                    `[i18n] MISSING KEY: ${key} (locales=${(lngs || []).join(",")})`,
                );
            }
        },
        // Only invoked when the key is missing in BOTH the active locale
        // AND the English fallback — at which point returning the key
        // path as the string is the right default.
        parseMissingKeyHandler: (key) =>
            IS_DEV ? `⚠ MISSING: ${key}` : key,
    });

export function missingKeys() {
    return Array.from(missingKeysSeen).sort();
}

export function setLocale(lng) {
    if (!SUPPORTED_LOCALES.includes(lng)) return;
    window.localStorage.setItem(LS_KEY, lng);
    i18n.changeLanguage(lng);
}

export function getLocale() {
    return i18n.resolvedLanguage || i18n.language || "en";
}

export default i18n;
